'use client';

import { useMemo } from 'react';
import { useScrollLock } from 'hooks/scroll/useScrollLock';
import { useTranslations } from 'next-intl';
import { TypeformSnippet } from 'components/shared/TypeformSnippet';
import {
  buildTypeformTrackingFromUrl,
  getPageUrlUtmSource,
} from 'lib/analytics/buildTypeformTrackingParams';
import styles from './PartnerTypeformModal.module.css';

/** https://form.typeform.com/to/UGGz7onQ — institutional research partnerships. */
export const PULSOMICS_PARTNER_TYPEFORM_ID = 'UGGz7onQ';

/* Locks document scroll for as long as the modal body is mounted. Performance
   Lab does this with PerformanceLabModalBodyScrollLock, which also pauses its
   Lenis instance — Pulsomics has no Lenis, so this is just the generic half. */

type PartnerTypeformModalProps = {
  /** Distinguishes which CTA opened the form; lands in the typeform as utm_medium. */
  source?: string;
  typeformId?: string;
};

/**
 * The institutional partnership form, embedded in the shared modal.
 *
 * Tracking mirrors Performance Lab: buildTypeformTrackingFromUrl forwards any
 * UTM/affiliate/click-id params already on the page URL into the typeform as
 * hidden fields, and fills the gaps with the defaults below — so a response
 * arrives knowing which page and which CTA produced it.
 *
 * It deliberately does NOT use PerformanceLabTypeformSnippet. That wrapper
 * fires a `performance_lab_form_submit` dataLayer event against the Performance
 * Lab Google Ads conversion (AW-614890616); firing it here would book Pulsomics
 * partnership enquiries as Performance Lab leads. If Pulsomics needs its own
 * conversion event, it wants its own GTM trigger and conversion label.
 */
export const PartnerTypeformModal = ({
  source = 'web',
  typeformId = PULSOMICS_PARTNER_TYPEFORM_ID,
}: PartnerTypeformModalProps) => {
  const t = useTranslations('common');
  useScrollLock(true);

  const trackingParams = useMemo(
    () =>
      buildTypeformTrackingFromUrl({
        utm_source: getPageUrlUtmSource('pulsomics'),
        utm_medium: source,
        utm_campaign: 'pulsomics_partnerships',
      }),
    [source],
  );

  return (
    <div className={styles.modalRoot}>
      <div className={styles.typeformContainer}>
        <TypeformSnippet
          typeformId={typeformId}
          frameTitle={t('pulsomics.partnerTypeform.frameTitle')}
          className="typeform-container"
          trackingParams={trackingParams}
        />
      </div>
    </div>
  );
};

export default PartnerTypeformModal;
